import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATE_FORMAT } from 'constants/date';
import {
  ApplicationImpoundAmountType,
  defaultImpoundAmountTypeOption,
  defaultMailAddressSourceOption,
  defaultPhysicalMailClassOption,
  FieldTypesEnum,
  formBundleReadinessStatusOptions,
  impoundAmountTypeOptions,
  LoanActionDateTypesEnum,
  LoanActionStaticItemsTypesEnum,
  LoanActionStaticValueSourceEnum,
  mailAddressSourceOptions,
  PdfSourceTypeEnum,
  pdfSourceTypeMap,
  physicalMailClassOptions,
} from 'constants/loanActionEditor';
import { LoanContactTypeEnum } from 'constants/loanContactTypes';
import { MismoDocumentType } from 'constants/mismo';
import { mismoDocumentTypeOptions } from 'constants/options';
import { getConditionalObject } from 'utils/data';

export const getGeneratePdfSourceTypeBodyToSend = ({ sourceType, form, formBundle }) =>
  ({
    [PdfSourceTypeEnum.FORM]: {
      formId: form?.value,
      id: form?.value,
      type: PdfSourceTypeEnum.FORM,
    },
    [PdfSourceTypeEnum.FORM_BUNDLE]: {
      formBundleId: formBundle?.value,
      id: formBundle?.value,
      type: PdfSourceTypeEnum.FORM_BUNDLE,
    },
  })[sourceType?.value] ?? null;

// A null documentType means the legacy behaviour: file the output under the type declared by the
// selected form or bundle. Sending it back unset preserves that for actions that never opt in.
export const getDocumentTypeSelectionBodyToSend = ({
  preSelectDocumentType,
  documentType,
  isDocumentTypeUserSelectable,
}) => {
  const documentTypeId = preSelectDocumentType ? (documentType?.value ?? null) : null;

  return documentTypeId || isDocumentTypeUserSelectable
    ? { documentTypeId, isUserSelectable: !!isDocumentTypeUserSelectable }
    : null;
};

// Create Document From Source shares the source row with Generate/Print PDF but is the only action
// that files its output under a chosen document type, so only it opts into those form keys.
export const getCreateDocumentFromSourceFormItem = args =>
  getPdfSourceFormItem({ ...args, withDocumentTypeSelection: true });

// A stored type absent from the options list (inactive, deleted, or a catalog fetch that failed) keeps its id
// on a synthetic option instead of loading as empty: the field is required, and validateForm covers the whole
// actions array, so an unresolvable row would otherwise block saving every other action in the group.
const resolveDocumentTypeOption = (documentTypesOptions, documentTypeId) =>
  documentTypesOptions?.find(option => option.value === documentTypeId) ?? {
    name: 'Unavailable document type',
    value: documentTypeId,
  };

export const getPdfSourceFormItem = ({
  actionItem,
  formsOptions,
  formBundlesOptions,
  documentTypesOptions,
  withDocumentTypeSelection = false,
}) => {
  const { id, type: sourceType } = actionItem.sourceType ?? {};
  const { documentTypeId, isUserSelectable } = actionItem.documentType ?? {};

  const sourceTypeDependentFieldNameAndOptionsMap = {
    [PdfSourceTypeEnum.FORM]: { fieldName: 'form', options: formsOptions },
    [PdfSourceTypeEnum.FORM_BUNDLE]: { fieldName: 'formBundle', options: formBundlesOptions },
  };

  const { fieldName, options } = sourceTypeDependentFieldNameAndOptionsMap[sourceType] ?? {};

  return [
    {
      preSelectSourceItem: !!id,
      sourceType: pdfSourceTypeMap[sourceType],
      ...getConditionalObject(withDocumentTypeSelection, () => ({
        preSelectDocumentType: !!documentTypeId,
        isDocumentTypeUserSelectable: !!isUserSelectable,
        ...getConditionalObject(documentTypeId, () => ({
          documentType: resolveDocumentTypeOption(documentTypesOptions, documentTypeId),
        })),
      })),
      ...getConditionalObject(fieldName, () => ({
        [fieldName]: options?.find(option => option.value === id),
      })),
    },
  ];
};

export const getMismoDocumentFormItem = actionItem => [
  {
    documentType: mismoDocumentTypeOptions.find(
      ({ value }) => value === (actionItem.documentType ?? MismoDocumentType.DEFAULT),
    ),
  },
];

export const getSetFormBundleReadinessStatusFormItem = ({ actionItem, formBundlesOptions }) => {
  const readinessStatus =
    formBundleReadinessStatusOptions.find(
      ({ value }) => value === actionItem.targetReadinessStatus,
    ) ?? null;
  const formBundleIds = (actionItem.formBundleIds ?? [])
    .map(id => formBundlesOptions.find(({ value }) => value === id))
    .filter(Boolean);

  return [{ readinessStatus, formBundleIds }];
};

export const getMailDocumentPackageApiFormat = ({ formItem, common }) => {
  const { formBundle, mailClass, addressSource } = formItem.mailDocumentPackage[0];

  return {
    ...common,
    formBundleId: formBundle?.value ?? null,
    mailClass: mailClass?.value ?? defaultPhysicalMailClassOption.value,
    addressSource: addressSource?.value ?? defaultMailAddressSourceOption.value,
  };
};

export const getMailDocumentPackageFormItem = ({ actionItem, formBundlesOptions }) => [
  {
    formBundle: formBundlesOptions.find(({ value }) => value === actionItem.formBundleId) ?? null,
    mailClass:
      physicalMailClassOptions.find(({ value }) => value === actionItem.mailClass) ??
      defaultPhysicalMailClassOption,
    addressSource:
      mailAddressSourceOptions.find(({ value }) => value === actionItem.addressSource) ??
      defaultMailAddressSourceOption,
  },
];

export const getUploadFormDeliveryApiFormat = ({ formItem, common }) => {
  const { formBundle, stopCic } = formItem.uploadFormDelivery[0];

  return {
    ...common,
    formBundleId: formBundle?.value ?? null,
    stopCic: !!stopCic,
  };
};

export const getUploadFormDeliveryFormItem = ({ actionItem, formBundlesOptions }) => [
  {
    formBundle: formBundlesOptions.find(({ value }) => value === actionItem.formBundleId) ?? null,
    stopCic: actionItem.stopCic ?? false,
  },
];

export const getApplicationImpoundApiFormat = ({
  formItem,
  common,
  buildExpressionTree,
  arithmeticOperationsSchemaParams,
  isNewExpressionsParsingLogicEnabled,
}) => {
  const { impoundType, amountType, monthlyAmount, monthlyAmountExpression } =
    formItem.setApplicationImpound[0];

  const isExpressionAmount =
    amountType?.value === ApplicationImpoundAmountType.EXPRESSION_CALCULATED;

  const expressionMonthlyAmount = monthlyAmountExpression
    ? {
        value: buildExpressionTree(monthlyAmountExpression, {
          schemaParams: arithmeticOperationsSchemaParams,
          isNewExpressionsParsingLogicEnabled,
        }),
        userFriendlyValue: monthlyAmountExpression,
      }
    : null;

  return {
    ...common,
    impoundTypeId: impoundType?.value,
    amount: isExpressionAmount
      ? {
          type: ApplicationImpoundAmountType.EXPRESSION_CALCULATED,
          monthlyAmount: expressionMonthlyAmount,
        }
      : { type: ApplicationImpoundAmountType.CONSTANT, monthlyAmount: Number(monthlyAmount) },
  };
};

export const getApplicationImpoundFormItem = ({ actionItem, impoundTypeOptions }) => {
  const isExpressionAmount =
    actionItem.amount?.type === ApplicationImpoundAmountType.EXPRESSION_CALCULATED;

  return [
    {
      impoundType:
        impoundTypeOptions.find(({ value }) => value === actionItem.impoundTypeId) ?? null,
      amountType:
        impoundAmountTypeOptions.find(({ value }) => value === actionItem.amount?.type) ??
        defaultImpoundAmountTypeOption,
      monthlyAmount:
        actionItem.amount?.type === ApplicationImpoundAmountType.CONSTANT
          ? (actionItem.amount.monthlyAmount ?? '')
          : '',
      monthlyAmountExpression: isExpressionAmount
        ? (actionItem.amount.monthlyAmount?.userFriendlyValue ?? '')
        : '',
    },
  ];
};

export const getContactApiFormatFromFormFormat = receiver => {
  if (receiver.value === LoanContactTypeEnum.LoanRole) {
    return { type: receiver.value, loanRoleId: receiver.loanRoleId };
  }

  if (receiver.value === LoanContactTypeEnum.SecurityGroup) {
    return { type: receiver.value, roleId: receiver.roleId };
  }

  return { type: receiver.value };
};

export const getContactFormFormatFromApiFormat = (contact, loanContactTypesOptionsMap) => {
  if (contact.type === LoanContactTypeEnum.LoanRole) {
    const loanRoleOption = loanContactTypesOptionsMap[contact.loanRoleId];

    return {
      ...loanRoleOption,
      value: LoanContactTypeEnum.LoanRole,
      loanRoleId: contact.loanRoleId,
    };
  }

  if (contact.type === LoanContactTypeEnum.SecurityGroup) {
    const securityGroupOption = loanContactTypesOptionsMap[contact.roleId];

    return {
      ...securityGroupOption,
      value: LoanContactTypeEnum.SecurityGroup,
      roleId: contact.roleId,
    };
  }

  return loanContactTypesOptionsMap[contact.type];
};

export const formatActionItemsFieldsInitialFormValues = ({ fieldValue, valueData }) => {
  switch (valueData.type) {
    case FieldTypesEnum.MULTISELECT:
      return valueData.options.filter(({ value }) => _.some(fieldValue, i => i.id === value));

    case FieldTypesEnum.SELECT:
      return valueData.options.find(({ value }) => value === fieldValue);

    case FieldTypesEnum.DATE:
      return fieldValue ? dayjs(fieldValue) : null;

    default:
      return fieldValue;
  }
};

export const formatActionItemsFieldsOutputFormValues = ({ fieldValue, valueData }) => {
  if (!fieldValue) {
    return fieldValue;
  }

  switch (valueData?.type) {
    case FieldTypesEnum.MULTISELECT:
      return _.map(fieldValue, i => ({ id: i.value, selectedBorrowers: [] }));

    case FieldTypesEnum.SELECT:
      return fieldValue.value;

    case FieldTypesEnum.DATE:
      return fieldValue.format(COMMON_DATE_FORMAT);

    default:
      return fieldValue;
  }
};

export const getSetApplicationValueItemType = ({ valueSource, dateType, fieldParams }) => {
  if (valueSource === LoanActionStaticValueSourceEnum.FIELD) {
    return LoanActionStaticItemsTypesEnum.SOURCE_FIELD;
  }

  if (valueSource === LoanActionStaticValueSourceEnum.NEXT_SEQUENCE) {
    return LoanActionStaticItemsTypesEnum.NEXT_SEQUENCE_VALUE;
  }

  if (
    dateType === LoanActionDateTypesEnum.EXECUTION_DATE &&
    fieldParams.type === FieldTypesEnum.DATE
  ) {
    return LoanActionStaticItemsTypesEnum.CURRENT_DATE;
  }

  return LoanActionStaticItemsTypesEnum.STATIC_VALUE;
};

const getSavePendingChangesPermissionsFormItem = permissions =>
  (permissions || []).map(({ expression, canCancel, canSave, canViewOthers }) => ({
    permissionFriendlyExpression: expression?.userFriendlyValue || '',
    canCancel: !!canCancel,
    canSave: !!canSave,
    canViewOthers: canViewOthers !== false,
  }));

export const getSavePendingChangesFormItem = ({ itemType, timedSequenceOptions, actionItem }) => ({
  itemType,
  timedSequenceOptions,
  permissions: getSavePendingChangesPermissionsFormItem(actionItem.permissions),
});

export const getRunFhaServiceFormItem = ({
  actionItem,
  fhaServiceTypeOptions,
  fhaServiceTypeModeOptionsMap,
  itemType,
  timedSequenceOptions,
}) => {
  const apiServiceType = actionItem.serviceType;
  const serviceTypeOption = fhaServiceTypeOptions.find(
    ({ value }) => value === apiServiceType?.type,
  );
  const modeOptions = fhaServiceTypeModeOptionsMap[apiServiceType?.type];
  const modeOption = modeOptions?.find(({ value }) => value === apiServiceType?.mode) ?? null;

  return {
    itemType,
    timedSequenceOptions,
    runFhaService: [{ serviceType: serviceTypeOption, mode: modeOption }],
  };
};
